<script setup lang="ts" generic="T extends string | number | symbol">
import {
  ref,
  onMounted,
  reactive,
  useSlots,
  computed,
  useTemplateRef,
} from 'vue';
import {
  useElementSize,
  useEventListener,
  useWindowScroll,
  useWindowSize,
} from '@vueuse/core';

import type {
  DropdownOpeningAlignment,
  DropdownOpeningDirection,
  DropdownConfig,
} from '../models/common.models';

import { calculateDropdownPosition } from './selects.helpers';

export interface SelectDropdownProps {
  parentRef: HTMLElement | null;
  isUsingTeleport?: boolean;
  wide?: boolean;
  dynamicHeight?: boolean;
}

const props = defineProps<SelectDropdownProps>();

const scrollYPosition = useWindowScroll().y;

const selectDropdownRef = useTemplateRef('selectDropdownRef');
const dropdownSize = useElementSize(selectDropdownRef);
const dropdownConfig = reactive<DropdownConfig>({
  topPosition: `${
    (props.parentRef?.getBoundingClientRect().top ?? 0) + scrollYPosition.value
  }px`,
  leftPosition: `${props.parentRef?.getBoundingClientRect().left ?? 0}px`,
  width: props.wide ? '100%' : '',
});

const dropdownOpeningDir = ref<DropdownOpeningDirection>('open-below');
const dropdownOpeningAli = ref<DropdownOpeningAlignment>('align-left');

const slots = useSlots();
const { height, width } = useWindowSize();
const hasFooter = computed(() => !!slots['footer']);

const DROPDOWN_MIN_SPACE = 300;

const hasMaxHeight = computed(() => {
  if (!props.dynamicHeight) {
    return true;
  }

  if (!props.parentRef) {
    return true;
  }

  const parentBounds = props.parentRef.getBoundingClientRect();
  const spaceBelow = height.value - parentBounds.bottom;
  const spaceAbove = parentBounds.top;

  return spaceBelow < DROPDOWN_MIN_SPACE && spaceAbove < DROPDOWN_MIN_SPACE;
});

onMounted(() => {
  if (props.parentRef) {
    const parentRefSizing = props.parentRef?.getBoundingClientRect();

    if (height.value < parentRefSizing?.bottom + dropdownSize.height.value) {
      dropdownOpeningDir.value = 'open-above';
    }

    const scrollbar = 18;
    if (
      width.value - scrollbar <
      dropdownSize.width.value + parentRefSizing?.left
    ) {
      dropdownOpeningAli.value = 'align-right';
    }
  }

  calculatePosition();
});

function calculatePosition(): void {
  if (props.parentRef && selectDropdownRef.value) {
    const containerBounds = props.parentRef.getBoundingClientRect();
    const dropdownBounds = selectDropdownRef.value.getBoundingClientRect();

    const { topPosition, leftPosition } = calculateDropdownPosition(
      containerBounds,
      dropdownOpeningAli.value,
      dropdownOpeningDir.value,
      scrollYPosition.value,
      dropdownBounds,
      props.isUsingTeleport
    );
    dropdownConfig.topPosition = topPosition;
    dropdownConfig.leftPosition = leftPosition;
  }
}

defineExpose({
  selectDropdownRef,
});

useEventListener(window, 'scroll', calculatePosition);
useEventListener(window, 'resize', calculatePosition);
</script>

<template>
  <div
    id="appSelectDropdown"
    ref="selectDropdownRef"
    class="app-select-dropdown"
    :style="{
      top: dropdownConfig.topPosition,
      left: dropdownConfig.leftPosition,
      width: dropdownConfig.width,
    }"
    :class="[
      {
        wide: props.wide,
      },
    ]"
  >
    <div class="dropdown-wrapper" :class="{ 'has-max-height': hasMaxHeight }">
      <slot name="options" />
    </div>
    <div v-if="hasFooter" class="footer-wrapper">
      <slot name="footer" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../../styles/media' as *;
@use '../../../styles/fonts' as *;

.app-select-dropdown {
  position: absolute;
  z-index: var(--panel-third-level-z-index);

  box-shadow: var(--shadow-elevation-30);
  @include font-label-m-light;
  background-color: var(--color-white);

  color: var(--color-neutral-70);
  border-radius: var(--border-radius-12);
  padding: var(--spacing-8);
  .dropdown-wrapper {
    min-width: 120px;

    &.has-max-height {
      max-height: 240px;
      overflow-y: auto;
    }
  }

  &.wide {
    .dropdown-wrapper {
      width: 100%;
    }
  }
}
</style>
